/**
 * Where the session token lives between page loads.
 *
 * `sessionStorage`, not `localStorage`, and the choice is not incidental. The
 * token is a bearer credential that the server cannot re-issue — it is returned
 * by `POST /auth/login` exactly once and the server keeps only its SHA-256
 * digest (`credentials.ts`). Persisting it across browser restarts would widen
 * the window in which a credential nobody can revoke-by-rotation sits on disk,
 * for the benefit of not retyping a password after a reboot. `sessionStorage`
 * keeps it to the tab and clears itself when the tab closes.
 *
 * Nothing here inspects the token. It is opaque, it is not a JWT, and the only
 * authority on who it belongs to and when it dies is `GET /auth/session`.
 *
 * Storage can throw — Safari's private mode, a browser configured to block site
 * data — so every access is guarded. A failure to read is treated as "no
 * session", which sends the visitor to the sign-in screen: the worst outcome is
 * signing in again, whereas an unhandled throw here is a blank page.
 */

const TOKEN_KEY = "halyard.session.token";
const ORGANISATION_KEY = "halyard.session.organisation";

function read(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // A session that cannot be persisted still works for this page load. The
    // cost is a sign-in on refresh, which is not worth failing the app over.
  }
}

function remove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Same reasoning as `write`.
  }
}

export function storedToken(): string | null {
  const token = read(TOKEN_KEY);
  return token === null || token === "" ? null : token;
}

export function storeToken(token: string): void {
  write(TOKEN_KEY, token);
}

/**
 * The organisation the person last acted within.
 *
 * A preference and nothing more — the server derives scope from the caller's own
 * memberships and a value here grants nothing (`credentials.ts`). It is stored
 * so that a refresh does not silently drop someone from the organisation they
 * were looking at into whichever one happens to be first; it is re-checked
 * against `memberships` on every restore, and discarded if it is not among them.
 */
export function storedOrganisationId(): string | null {
  const id = read(ORGANISATION_KEY);
  return id === null || id === "" ? null : id;
}

export function storeOrganisationId(id: string | null): void {
  if (id === null) {
    remove(ORGANISATION_KEY);
    return;
  }
  write(ORGANISATION_KEY, id);
}

/** Forget everything. Called on sign-out and whenever the server says 401. */
export function clearStoredSession(): void {
  remove(TOKEN_KEY);
  remove(ORGANISATION_KEY);
}
